import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  ArrowLeft,
  MailOpen,
  MousePointerClick,
  XCircle,
  Send,
  Monitor,
  Smartphone,
  Tablet as TabletIcon,
  Bot,
  Users,
  Globe,
  ExternalLink,
  Clock,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const CHART_COLORS = ["#004030", "#7a8a5c", "#c9a13a", "#4e6789", "#a15939", "#556b6b"];

function Stat({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {label}
        </span>
        <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-3xl mt-4">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </div>
  );
}

function DeviceIcon({ type }) {
  const cls = "w-4 h-4 text-muted-foreground";
  if (type === "mobile") return <Smartphone className={cls} strokeWidth={1.5} />;
  if (type === "tablet") return <TabletIcon className={cls} strokeWidth={1.5} />;
  if (type === "bot") return <Bot className={cls} strokeWidth={1.5} />;
  return <Monitor className={cls} strokeWidth={1.5} />;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "Europe/Rome",
    });
  } catch {
    return iso;
  }
}

function fmtHour(bucket) {
  // bucket is UTC hour like "2026-02-22T09" (produced by $substr[0:13] on ISO UTC timestamps)
  // We need to convert to Europe/Rome for display.
  if (!bucket) return "";
  try {
    // Parse as UTC by appending :00:00Z
    const asUtc = new Date(bucket + ":00:00Z");
    return asUtc.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Rome",
    });
  } catch {
    return bucket;
  }
}

export default function CampaignReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEvent, setDrawerEvent] = useState(null);
  const [drawerHits, setDrawerHits] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  useEffect(() => {
    api.get(`/campaigns/${id}`).then(({ data }) => setC(data));
    api.get(`/campaigns/${id}/events`).then(({ data }) => setEvents(data));
    api.get(`/campaigns/${id}/analytics`).then(({ data }) => setAnalytics(data));
  }, [id]);

  const openDrawer = async (ev) => {
    setDrawerEvent(ev);
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerHits([]);
    try {
      const { data } = await api.get(`/campaigns/${id}/hits`, {
        params: { contact_id: ev.contact_id, limit: 500 },
      });
      setDrawerHits(data.items || []);
    } finally {
      setDrawerLoading(false);
    }
  };

  const st = c?.stats || {};
  const openRate = st.sent ? ((st.opens / st.sent) * 100).toFixed(1) : 0;
  const clickRate = st.sent ? ((st.clicks / st.sent) * 100).toFixed(1) : 0;

  const timelineData = useMemo(() => {
    const t = analytics?.timeline || [];
    const c = analytics?.click_timeline || [];
    const map = new Map();
    for (const x of t) map.set(x._id, { hour: x._id, opens: x.count, clicks: 0 });
    for (const x of c) {
      const rec = map.get(x._id) || { hour: x._id, opens: 0, clicks: 0 };
      rec.clicks = x.count;
      map.set(x._id, rec);
    }
    return Array.from(map.values()).sort((a, b) => a.hour.localeCompare(b.hour));
  }, [analytics]);

  if (!c) return <div className="p-8 text-muted-foreground">Caricamento…</div>;

  return (
    <>
      <PageHeader
        title={c.name}
        subtitle={c.subject}
        testid="report-header"
        actions={
          <Button
            variant="outline"
            onClick={() => navigate("/campagne")}
            data-testid="report-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Campagne
          </Button>
        }
      />
      <PageBody>
        <div className="mb-4 flex items-center gap-3">
          <Badge>Inviata</Badge>
          {c.sent_at && (
            <span className="text-xs text-muted-foreground">
              {fmtDate(c.sent_at)}
            </span>
          )}
        </div>

        {/* KPI */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat
            label="Destinatari"
            value={st.recipients || 0}
            sub={`${st.sent || 0} consegnate`}
            icon={Send}
            testid="report-recipients"
          />
          <Stat
            label="Aperture totali"
            value={st.opens || 0}
            sub={`${openRate}% · ${analytics?.unique_opens || 0} unici`}
            icon={MailOpen}
            testid="report-opens"
          />
          <Stat
            label="Click totali"
            value={st.clicks || 0}
            sub={`${clickRate}% · ${analytics?.unique_clickers || 0} unici`}
            icon={MousePointerClick}
            testid="report-clicks"
          />
          <Stat
            label="Disiscritti"
            value={st.unsubscribes || 0}
            sub={`${st.failed || 0} falliti`}
            icon={XCircle}
            testid="report-unsub"
          />
        </div>

        <Tabs defaultValue="analytics" className="mb-8">
          <TabsList className="w-full justify-start bg-transparent border-b border-border rounded-none">
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              Analytics
            </TabsTrigger>
            <TabsTrigger value="recipients" data-testid="tab-recipients">
              Destinatari ({events.length})
            </TabsTrigger>
          </TabsList>

          {/* ================= ANALYTICS ================= */}
          <TabsContent value="analytics" className="mt-6 space-y-6">
            {/* Timeline */}
            <div className="border border-border bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg">Timeline aperture &amp; click</h3>
                  <p className="text-xs text-muted-foreground">
                    Distribuzione oraria degli eventi
                  </p>
                </div>
                <Clock className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <div style={{ width: "100%", height: 260 }}>
                {timelineData.length === 0 ? (
                  <div className="h-full grid place-items-center text-sm text-muted-foreground">
                    Nessun evento ancora
                  </div>
                ) : (
                  <ResponsiveContainer>
                    <AreaChart data={timelineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e3e3e0" />
                      <XAxis
                        dataKey="hour"
                        tickFormatter={fmtHour}
                        stroke="#686865"
                        fontSize={11}
                      />
                      <YAxis stroke="#686865" fontSize={11} allowDecimals={false} />
                      <Tooltip
                        labelFormatter={fmtHour}
                        contentStyle={{
                          background: "#fff",
                          border: "1px solid #e3e3e0",
                          fontSize: 12,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="opens"
                        name="Aperture"
                        stroke="#004030"
                        fill="#004030"
                        fillOpacity={0.15}
                      />
                      <Area
                        type="monotone"
                        dataKey="clicks"
                        name="Click"
                        stroke="#c9a13a"
                        fill="#c9a13a"
                        fillOpacity={0.15}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Countries */}
              <div className="border border-border bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg">Nazioni</h3>
                    <p className="text-xs text-muted-foreground">
                      Top 10 per aperture
                    </p>
                  </div>
                  <Globe className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                {(analytics?.countries || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun dato geografico.</p>
                ) : (
                  <div className="space-y-2">
                    {analytics.countries.map((row, i) => {
                      const max = analytics.countries[0].count;
                      const pct = Math.round((row.count / max) * 100);
                      return (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="w-8 text-xs uppercase text-muted-foreground">
                            {row._id.cc || "??"}
                          </span>
                          <span className="flex-1 truncate">{row._id.country || "Sconosciuta"}</span>
                          <div className="w-32 bg-muted h-2 rounded-sm overflow-hidden">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="w-8 text-right tabular-nums">{row.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Cities */}
              <div className="border border-border bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg">Città</h3>
                    <p className="text-xs text-muted-foreground">Top 10 per aperture</p>
                  </div>
                </div>
                {(analytics?.cities || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun dato disponibile.</p>
                ) : (
                  <div className="space-y-2">
                    {analytics.cities.map((row, i) => {
                      const max = analytics.cities[0].count;
                      const pct = Math.round((row.count / max) * 100);
                      return (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="flex-1 truncate">
                            {row._id.city}
                            <span className="text-xs text-muted-foreground ml-1">
                              {row._id.country && `· ${row._id.country}`}
                            </span>
                          </span>
                          <div className="w-32 bg-muted h-2 rounded-sm overflow-hidden">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="w-8 text-right tabular-nums">{row.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Devices */}
              <div className="border border-border bg-white p-6">
                <h3 className="font-heading text-lg mb-1">Dispositivi</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Distribuzione delle aperture
                </p>
                {(analytics?.devices || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun dato.</p>
                ) : (
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={analytics.devices.map((d) => ({ name: d._id, value: d.count }))}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="value"
                          label={(e) => `${e.name} (${e.value})`}
                          labelLine={false}
                        >
                          {analytics.devices.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e3e3e0", fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Browsers */}
              <div className="border border-border bg-white p-6">
                <h3 className="font-heading text-lg mb-1">Browser</h3>
                <p className="text-xs text-muted-foreground mb-4">Top 8</p>
                {(analytics?.browsers || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun dato.</p>
                ) : (
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <BarChart data={analytics.browsers.map((b) => ({ name: b._id || "?", count: b.count }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e3e3e0" />
                        <XAxis dataKey="name" stroke="#686865" fontSize={11} />
                        <YAxis stroke="#686865" fontSize={11} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e3e3e0", fontSize: 12 }} />
                        <Bar dataKey="count" fill="#004030" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* OS */}
              <div className="border border-border bg-white p-6">
                <h3 className="font-heading text-lg mb-1">Sistemi Operativi</h3>
                <p className="text-xs text-muted-foreground mb-4">Top 8</p>
                {(analytics?.oses || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun dato.</p>
                ) : (
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <BarChart data={analytics.oses.map((o) => ({ name: o._id || "?", count: o.count }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e3e3e0" />
                        <XAxis dataKey="name" stroke="#686865" fontSize={11} />
                        <YAxis stroke="#686865" fontSize={11} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e3e3e0", fontSize: 12 }} />
                        <Bar dataKey="count" fill="#7a8a5c" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Top URLs */}
              <div className="border border-border bg-white p-6">
                <h3 className="font-heading text-lg mb-1">Link più cliccati</h3>
                <p className="text-xs text-muted-foreground mb-4">Top 10</p>
                {(analytics?.top_urls || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun click ancora.</p>
                ) : (
                  <div className="space-y-2">
                    {analytics.top_urls.map((row, i) => {
                      const max = analytics.top_urls[0].count;
                      const pct = Math.round((row.count / max) * 100);
                      return (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                          <a
                            href={row._id}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 truncate text-primary hover:underline"
                            title={row._id}
                          >
                            {row._id}
                          </a>
                          <div className="w-24 bg-muted h-2 rounded-sm overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-8 text-right tabular-nums">{row.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ISPs */}
            {(analytics?.isps || []).length > 0 && (
              <div className="border border-border bg-white p-6">
                <h3 className="font-heading text-lg mb-4">Provider / ISP</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                  {analytics.isps.map((row, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-border pb-2">
                      <span className="truncate mr-2" title={row._id}>{row._id}</span>
                      <span className="text-xs text-muted-foreground">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ================= RECIPIENTS ================= */}
          <TabsContent value="recipients" className="mt-6">
            <div className="border border-border bg-white">
              <div className="border-b border-border px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-lg">Attività per destinatario</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Clicca su una riga per vedere tutti gli eventi con IP, dispositivo, geolocalizzazione
                  </p>
                </div>
                <Users className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Aperture</TableHead>
                    <TableHead>Click</TableHead>
                    <TableHead>Prima apertura</TableHead>
                    <TableHead>Ultima attività</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((e) => (
                    <TableRow
                      key={e.id}
                      data-testid={`event-${e.id}`}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => openDrawer(e)}
                    >
                      <TableCell className="font-medium">{e.email}</TableCell>
                      <TableCell>
                        {e.status === "sent" ? (
                          <Badge variant="secondary">Consegnata</Badge>
                        ) : (
                          <Badge variant="destructive">Fallita</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {e.opens > 0 ? (
                          <span className="text-primary font-medium">{e.opens}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {e.clicks > 0 ? (
                          <span className="text-primary font-medium">{e.clicks}</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fmtDate(e.first_opened_at)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fmtDate(e.last_clicked_at || e.last_opened_at || e.sent_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                        Nessun destinatario ancora
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

        {/* ================= Drill-down drawer ================= */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="hits-drawer">
            <SheetHeader>
              <SheetTitle>{drawerEvent?.email}</SheetTitle>
              <SheetDescription>
                Cronologia completa degli eventi di tracking per questo destinatario
              </SheetDescription>
            </SheetHeader>

            {drawerLoading && (
              <p className="text-sm text-muted-foreground mt-6">Caricamento…</p>
            )}
            {!drawerLoading && drawerHits.length === 0 && (
              <p className="text-sm text-muted-foreground mt-6">
                Nessun evento di tracking registrato per questo destinatario.
              </p>
            )}
            {!drawerLoading && drawerHits.length > 0 && (
              <div className="space-y-3 mt-6">
                {drawerHits.map((h, i) => (
                  <div
                    key={i}
                    className="border border-border bg-white p-4"
                    data-testid={`hit-${i}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {h.kind === "open" && (
                          <Badge className="bg-primary text-primary-foreground">
                            <MailOpen className="w-3 h-3 mr-1" /> Apertura
                          </Badge>
                        )}
                        {h.kind === "click" && (
                          <Badge className="bg-[#c9a13a] text-white">
                            <MousePointerClick className="w-3 h-3 mr-1" /> Click
                          </Badge>
                        )}
                        {h.kind === "unsubscribe" && (
                          <Badge variant="destructive">
                            <XCircle className="w-3 h-3 mr-1" /> Disiscrizione
                          </Badge>
                        )}
                        {h.is_bot && <Badge variant="outline">Bot</Badge>}
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {fmtDate(h.timestamp)}
                      </span>
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div>
                        <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          IP
                        </dt>
                        <dd className="font-mono">{h.ip || "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Nazione
                        </dt>
                        <dd>
                          {h.country ? `${h.country} (${h.country_code})` : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Città
                        </dt>
                        <dd>{h.city ? `${h.city}, ${h.region || ""}` : "—"}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Dispositivo
                        </dt>
                        <dd className="flex items-center gap-1">
                          <DeviceIcon type={h.device_type} />
                          <span>{h.device_type || "unknown"}</span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Sistema
                        </dt>
                        <dd>
                          {h.os_family
                            ? `${h.os_family}${h.os_version ? " " + h.os_version : ""}`
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          Browser
                        </dt>
                        <dd>
                          {h.browser_family
                            ? `${h.browser_family}${h.browser_version ? " " + h.browser_version : ""}`
                            : "—"}
                        </dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                          ISP
                        </dt>
                        <dd className="truncate" title={h.isp || ""}>
                          {h.isp || "—"}
                        </dd>
                      </div>
                      {h.target_url && (
                        <div className="col-span-2">
                          <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                            Link cliccato
                          </dt>
                          <dd className="truncate">
                            <a
                              href={h.target_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary hover:underline"
                            >
                              {h.target_url}
                            </a>
                          </dd>
                        </div>
                      )}
                      {h.user_agent && (
                        <div className="col-span-2">
                          <dt className="text-muted-foreground uppercase tracking-wider text-[10px]">
                            User-Agent
                          </dt>
                          <dd className="font-mono text-[11px] break-all text-muted-foreground">
                            {h.user_agent}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </PageBody>
    </>
  );
}
